import { useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

// (The preview image URL is built from the public Supabase project URL —
// no client needed for that part; supabase is used for the RPC lookups.)

// ── Link-preview metadata for /road/[id] and /route/[id] deep links ─────────
// Link-card crawlers (iMessage, WhatsApp, Slack…) execute the page's JS, so
// the title and preview image are set dynamically once the shared entity is
// resolved: title "Roadratings - [road/route name]", image = the og-map Edge
// Function's rendered map thumbnail centered on that road/route.

interface OgRoad {
  id: string
  name: string
  group?: string | null
  region?: string | null
  distance_miles?: number | null
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function ogImageUrl(kind: 'road' | 'route', publicId: string): string {
  const url = import.meta.env.VITE_SUPABASE_URL ?? ''
  const base = url.replace(/\/$/, '')
  return `${base}/functions/v1/og-map?kind=${kind}&id=${encodeURIComponent(publicId)}`
}

export function useDeepLinkOg(publicId: string | undefined) {
  useEffect(() => {
    if (!publicId) return
    const isRoute = window.location.pathname.startsWith('/route/')
    const kind: 'road' | 'route' = isRoute ? 'route' : 'road'
    let cancelled = false
    const fallbackTitle = document.title

    const apply = (title: string, description: string) => {
      if (cancelled) return
      document.title = title
      const image = ogImageUrl(kind, publicId)
      setMeta('property', 'og:title', title)
      setMeta('name', 'twitter:title', title)
      setMeta('property', 'og:description', description)
      setMeta('name', 'twitter:description', description)
      setMeta('property', 'og:image', image)
      setMeta('name', 'twitter:image', image)
      setMeta('property', 'og:type', 'website')
      setMeta('name', 'twitter:card', 'summary_large_image')
    }

    if (kind === 'road') {
      void supabase
        .rpc('og_deep_link_data', { _public_id: publicId, _kind: 'road' })
        .then(({ data }) => {
          const road = (data as { road?: OgRoad } | null)?.road
          const name = road?.name ?? 'Road'
          const where = [road?.group, road?.region].filter(Boolean).join(' · ')
          const miles = road?.distance_miles
          const desc = [
            where,
            typeof miles === 'number' ? `${miles} mi` : null,
            'Rated roads for motorcycle riders.',
          ]
            .filter(Boolean)
            .join(' — ')
          apply(`Roadratings - ${name}`, desc)
        })
    } else {
      void supabase
        .rpc('og_deep_link_data', { _public_id: publicId, _kind: 'route' })
        .then(({ data }) => {
          const info = data as { name?: string; road_ids?: string[] } | null
          const name = info?.name ?? 'Shared route'
          const count = Array.isArray(info?.road_ids) ? info!.road_ids!.length : 0
          apply(
            `Roadratings - ${name}`,
            `${count} road${count === 1 ? '' : 's'} — a shared RoadRatings route.`,
          )
        })
    }

    return () => {
      cancelled = true
      document.title = fallbackTitle
    }
  }, [publicId])
}

export default useDeepLinkOg
